import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { useLean } from '../components/ui/depth.tsx';
import { WagmiProvider } from 'wagmi';
import { ConnectModalProvider } from '../components/layout/ConnectModal.tsx';
import { Shell } from '../components/layout/Shell.tsx';
import { ToastProvider } from '../components/ui/toasts.tsx';
import { ChainClockProvider } from '../hooks/useChainClock.tsx';
import { wagmiConfig } from '../config/wagmi.ts';
import { I18nProvider, useI18n, type MessageKey } from '../i18n/index.tsx';
import { Admin } from '../pages/Admin.tsx';
import { Bond } from '../pages/Bond.tsx';
import { Dashboard } from '../pages/Dashboard.tsx';
import { Docs } from '../pages/Docs.tsx';
import { Liquidity } from '../pages/Liquidity.tsx';
import { parseInvitePath, Referral } from '../pages/Referral.tsx';
import { Stake } from '../pages/Stake.tsx';
import { Swap } from '../pages/Swap.tsx';
import { navigate, useLocation } from './router.tsx';
import { rememberInvite } from '../hooks/useInvite.ts';

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 5_000, retry: 1 } } });

interface Route {
  title: MessageKey;
  sub: MessageKey;
  render(): ReactNode;
}

const ROUTES: Record<string, Route> = {
  '/': { title: 'page.dashboard.title', sub: 'page.dashboard.sub', render: () => <Dashboard /> },
  '/stake': { title: 'page.stake.title', sub: 'page.stake.sub', render: () => <Stake /> },
  '/bond': { title: 'page.bond.title', sub: 'page.bond.sub', render: () => <Bond /> },
  '/swap': { title: 'page.swap.title', sub: 'page.swap.sub', render: () => <Swap /> },
  '/liquidity': { title: 'page.liquidity.title', sub: 'page.liquidity.sub', render: () => <Liquidity /> },
  '/referral': { title: 'page.referral.title', sub: 'page.referral.sub', render: () => <Referral /> },
  '/docs': { title: 'page.docs.title', sub: 'page.docs.sub', render: () => <Docs /> },
  '/admin': { title: 'page.admin.title', sub: 'page.admin.sub', render: () => <Admin /> },
};

function Routed() {
  const { path } = useLocation();
  const { t } = useI18n();
  const invite = parseInvitePath(path);
  // an invite link (/r/<code>/<side>) opens the referral page with the code filled in, and the URL keeps it until bound
  useEffect(() => {
    if (!invite) return;
    rememberInvite(invite.code, invite.side);
    const q = new URLSearchParams(window.location.search);
    q.set('ref', invite.code);
    q.set('side', invite.side);
    navigate(`/referral?${q}`, true);
  }, [invite?.code, invite?.side]);
  const route = invite ? ROUTES['/referral'] : (ROUTES[path] ?? ROUTES['/']);
  const title = t(route.title);
  useEffect(() => {
    document.title = `${title} · Syntra DAO`;
  }, [title]);
  return (
    <Shell title={title} subtitle={t(route.sub)}>
      {route.render()}
    </Shell>
  );
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <ChainClockProvider>
        <I18nProvider>
          <ToastProvider>
            <ConnectModalProvider>{children}</ConnectModalProvider>
          </ToastProvider>
        </I18nProvider>
        </ChainClockProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export function App() {
  useLean();
  return (
    <Providers>
      <Routed />
    </Providers>
  );
}
